import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import useDialogStore from "../../../stores/useDialogStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  ChevronUpIcon,
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  QrCodeIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import dayjs from "dayjs";
import EditableDateCell from "../../../components/EditableDateCell";
import ColumnSearch from "../../../components/ColumnSearch";
import excelIcon from "../../../assets/excel-icon.webp";
import InvoiceQRScanner from "../../../components/InvoiceQRScanner";
import ColumnSelector from "../../../components/ColumnSelector";
import QReader from "../../../components/QReader";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";
import React from "react";
import DeliveryNoteModal from "./DeliveryNoteModal";

const AddToReservationModal = ({ invoiceNumber, onClose }) => {
  const [uniqueIdsText, setUniqueIdsText] = useState("");
  const queryClient = useQueryClient();

  const addDevicesMutation = useMutation({
    mutationFn: async (uniqueIds) => {
      return await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/add-to-reservation`,
        {
          invoice_number: invoiceNumber,
          unique_ids: uniqueIds,
        }
      );
    },
    onSuccess: () => {
      toast.success("Devices added to reservation successfully!");
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to add devices.");
    },
  });

  const handleSubmit = () => {
    const uniqueIds = uniqueIdsText
      .split("\n")
      .map((id) => id.trim())
      .filter((id) => id !== "");

    if (uniqueIds.length === 0) {
      toast.error("Please enter at least one unique ID.");
      return;
    }

    addDevicesMutation.mutate(uniqueIds);
  };

  return (
    <div className="space-y-4 p-2">
      <p className="text-sm text-gray-600">
        Adding devices to reservation: <span className="font-bold text-blue-600">{invoiceNumber}</span>
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Unique IDs (one per line)
        </label>
        <textarea
          value={uniqueIdsText}
          onChange={(e) => setUniqueIdsText(e.target.value)}
          rows={6}
          placeholder={`00001\n00002\n00003`}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="light" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          isLoading={addDevicesMutation.isPending}
        >
          {addDevicesMutation.isPending ? "Adding..." : "Add to Reservation"}
        </Button>
      </div>
    </div>
  );
};

export default function ReservedStock() {
  const { openDialog, closeDialog, getIsOpen } = useDialogStore();
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [exportInvoiceNumber, setExportInvoiceNumber] = useState(null);

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
    invoice_number: true,
    date_reserved: true,
    confirmed: true,
    client_id: true,
    client: true,
    weight: true,
    real_price: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "date_reserved", label: "Date Reserved" },
    { key: "confirmed", label: "Confirmed" },
    { key: "client_id", label: "Client ID" },
    { key: "client", label: "Client" },
    { key: "weight", label: "Weight" },
    { key: "real_price", label: "Price" },
  ];

  const [openDisclosure, setOpenDisclosure] = useState({
    invoiceNumber: null,
    invoiceId: null,
  });

  const { data: invoiceDetails, isLoading: invoiceDetailsLoading } = useQuery({
    queryKey: ["invoiceDetails", openDisclosure, location.search],
    queryFn: async () => {
      if (!openDisclosure) return null;

      const params = new URLSearchParams(location.search);
      const uniqueId = params.get("unique_id");
      const invoiceId =
        openDisclosure?.invoiceId || params.get("invoice_id") || null;
      const invoiceNumber =
        openDisclosure?.invoiceNumber || params.get("invoice_number");
      const queryParam = uniqueId
        ? `unique_id=${uniqueId}&invoice_id=${invoiceId}`
        : "type=all";

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/by-invoice-number/${invoiceNumber}?${queryParam}`
      );
      return response.data;
    },
    enabled: !!openDisclosure?.invoiceNumber,
    keepPreviousData: true,
  });

  const handleDisclosureToggle = (invoiceNumber, invoiceId) => {
    if (openDisclosure?.invoiceNumber === invoiceNumber) {
      setOpenDisclosure(null);
    } else {
      setOpenDisclosure(null);
      setTimeout(() => {
        setOpenDisclosure({ invoiceNumber, invoiceId });
      }, 150);
    }
  };

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const { data: devicesData, isLoading } = useQuery({
    queryKey: ["reserved-devices", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/devices/status/reserved?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const editStatusCellMutation = useMutation({
    mutationFn: ({ invoiceNumber, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/patch/${invoiceNumber}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const markAsSoldMutation = useMutation({
    mutationFn: (invoiceNumber) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/mark-as-sold`,
        { invoice_number: invoiceNumber }
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(response.data.message);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to mark devices as sold."
      );
      console.error("Mark as sold error:", error);
    },
  });

  useEffect(() => {
    if (!getIsOpen()) {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
    }
  }, [getIsOpen()]);

  const removeReservationMutation = useMutation({
    mutationFn: (invoiceNumber) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/delete-by-invoice-number/${invoiceNumber}`
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(response.data.message);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to remove reservation."
      );
      console.error("Remove reservation error:", error);
    },
  });

  const deleteSingleDeviceMutation = useMutation({
    mutationFn: (deviceId) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/${deviceId}`
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(
        response.data.message || "Device reservation cancelled successfully"
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to cancel device reservation."
      );
      console.error("Delete single device error:", error);
    },
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        {exportInvoiceNumber && (
          <div className="mb-4">
            <h3 className="text-lg">
              Export only devices with invoice number: <br />
              <span className="font-bold text-blue-600">
                {exportInvoiceNumber}
              </span>
            </h3>
          </div>
        )}
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={() => closeDialog()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setSelectedColumns(localSelectedColumns);
              onExport(localSelectedColumns);
              closeDialog();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {exportMutation.isPending ? (
              <div className="flex items-center gap-2">Exporting...</div>
            ) : (
              "Export"
            )}
          </button>
        </div>
      </div>
    );
  };

  const exportCSVData = (invoice_number) => {
    setExportInvoiceNumber(invoice_number);
    openDialog({
      title: "Select Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns) => {
            handleExport(finalColumns);
          }}
        />
      ),
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      if (exportInvoiceNumber) {
        params.set("invoice_number", exportInvoiceNumber);
      }
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/reserved-devices`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
          invoice_number: exportInvoiceNumber || null,
        },
        { responseType: "blob" }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const date = new Date()
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-");
      const invoiceNumber = exportInvoiceNumber;
      const filename = invoiceNumber
        ? `reserved_stock_${date}_invoice_${invoiceNumber}.xlsx`
        : `reserved_stock_export_${date}.xlsx`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setExportInvoiceNumber(null);
      closeDialog();
      toast.success("Report exported successfully!");
      handleClearFilters();
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  // ── AbaNinja ──────────────────────────────────────────────────────────────

  // Shared hook: check if an AbaNinja invoice exists for a given invoice number
  const useAbaNinjaInvoiceCheck = (invoiceNumber) => {
    return useQuery({
      queryKey: ["abaninja-invoice-check", invoiceNumber],
      queryFn: async () => {
        if (!invoiceNumber) return null;
        try {
          const response = await axiosInstance.get(
            `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/by-invoice-number/${invoiceNumber}`
          );
          return response.data;
        } catch (error) {
          if (error.response?.status === 404) {
            return null;
          }
          throw error;
        }
      },
      enabled: !!invoiceNumber,
      staleTime: 5 * 60 * 1000,
    });
  };

  const updateAbaNinjaInvoiceMutation = useMutation({
    mutationFn: async ({ id, data }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/${id}`,
        data
      ),
    onSuccess: () => {
      toast.success("AbaNinja invoice updated successfully!");
      queryClient.invalidateQueries(["abaninja-invoice-check"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to update AbaNinja invoice."
      );
    },
  });

  const sendToAbaNinjaMutation = useMutation({
    mutationFn: async (data) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/abaninja/send-invoice`,
        data
      ),
    onSuccess: () => {
      toast.success("Successfully sent to AbaNinja!");
      closeDialog();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to save AbaNinja record."
      );
    },
  });

  const AbaNinjaModal = ({ invoiceNumber, invoiceId, initialFreightPrice = "" }) => {
    const [abaForm, setAbaForm] = useState({
      used_photocopiers: "", // populated via useEffect only for EX clients
      photocopiers_sets: "",
      finishers_count: "",
      collis_count: "",
      weight: "",
      payment_terms: "In advance",
      made_in: "China",
      real_price: "",
    });

    const [isSending, setIsSending] = useState(false);
    const [frachtExportPrice, setFrachtExportPrice] = useState(String(initialFreightPrice));
    const [showFrachtExport, setShowFrachtExport] = useState(true);
    const [localPrices, setLocalPrices] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [showBulkAddDialog, setShowBulkAddDialog] = useState(false);

    const { data: existingInvoice, isLoading: checkingExisting } =
      useAbaNinjaInvoiceCheck(invoiceNumber);

    // Update device invoice (used for Fracht Export price on EX clients)
    const updateDeviceInvoiceMutation = useMutation({
      mutationFn: ({ id, data }) =>
        axiosInstance.patch(
          `${window.REACT_APP_API_ENDPOINT}/abaninja/update-device-invoice/${id}`,
          data
        ),
      onSuccess: () => {
        toast.success("Fracht Export price updated!");
      },
      onError: () => {
        toast.error("Failed to update Fracht Export price.");
      },
    });

    const { data: abaDevices, isLoading: abaLoading } = useQuery({
      queryKey: ["abaninja-devices", invoiceNumber],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `${window.REACT_APP_API_ENDPOINT}/device-statuses/by-invoice-number/${invoiceNumber}?type=all`
        );
        return response.data;
      },
      enabled: !!invoiceNumber,
    });

    // Bulk-update price for all devices sharing the same brand+model in this invoice
    const bulkUpdatePriceMutation = useMutation({
      mutationFn: ({ brand, model, price }) =>
        axiosInstance.patch(
          `${window.REACT_APP_API_ENDPOINT}/devices/bulk-price`,
          { invoice_number: invoiceNumber, brand, model, price }
        ),
      onSuccess: () => {
        queryClient.invalidateQueries(["abaninja-devices"]);
        toast.success("Price updated for all matching devices!");
      },
      onError: () => {
        toast.error("Failed to update device prices.");
      },
    });

    const bulkUpdateByUniqueIdMutation = useMutation({
      mutationFn: ({ uniqueIds, prices }) =>
        axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/device-statuses/bulk-update-by-unique-id`,
          { invoice_number: invoiceNumber, unique_ids: uniqueIds, prices }
        ),
      onSuccess: (response) => {
        queryClient.invalidateQueries(["abaninja-devices"]);
        toast.success(response.data.message || "Devices updated successfully!");
        setShowBulkAddDialog(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Failed to update devices.");
      },
    });

    // Group devices by brand+model — one row per group in the table.
    // Also collect all distinct non-null prices in the group so we can
    // detect inconsistency (e.g. device 1 priced, device 2 null).
    const groupedDevices = React.useMemo(() => {
      if (!abaDevices?.data?.data) return [];
      const map = {};
      abaDevices.data.data.forEach((device) => {
        const key = `${device.brand}||${device.model}`;
        if (!map[key]) {
          map[key] = {
            key,
            brand: device.brand,
            model: device.model,
            count: 0,
            deviceIds: [],
            price: device.price ?? "",
            prices: [], // collect every raw price to detect inconsistency
          };
        }
        map[key].count += 1;
        map[key].deviceIds.push(device.id);
        map[key].prices.push(device.price);
      });
      return Object.values(map);
    }, [abaDevices]);

    // Initialize localPrices from groupedDevices
    useEffect(() => {
      if (groupedDevices.length > 0) {
        const initialPrices = {};
        groupedDevices.forEach((g) => {
          initialPrices[g.key] = g.price ?? "";
        });
        setLocalPrices(initialPrices);
      }
    }, [groupedDevices]);

    // Derive market type from the loaded devices so the render can use it
    const modalMarketType = abaDevices?.client_market_type
      ?? abaDevices?.data?.data?.[0]?.client_market_type
      ?? null;
    const isExCustomer = modalMarketType === "EX";

    // Total price of all device positions (used to seed Fracht Export default)
    const devicesTotal = React.useMemo(() => {
      return groupedDevices.reduce((sum, g) => sum + (parseFloat(g.price) || 0) * g.count, 0);
    }, [groupedDevices]);

    // ── Auto-sync: when modal loads, fix any group where some devices have a
    // price set and others are null — silently bulk-update to the known price.
    useEffect(() => {
      if (!groupedDevices.length) return;
      groupedDevices.forEach((group) => {
        const nonNullPrices = group.prices.filter((p) => p !== null && p !== "" && p !== undefined);
        const hasNull = group.prices.some((p) => p === null || p === "" || p === undefined);

        // Inconsistent: at least one priced and at least one null
        if (nonNullPrices.length > 0 && hasNull) {
          const resolvedPrice = nonNullPrices[0]; // use the first known price
          bulkUpdatePriceMutation.mutate({
            brand: group.brand,
            model: group.model,
            price: resolvedPrice,
          });
        }
      });
      // Only run when groupedDevices changes (i.e. on fresh data load)
    }, [groupedDevices]);

    useEffect(() => {
      if (!abaDevices?.data?.data) return;
      const devices = abaDevices.data.data;
      const numberOfDevices = abaDevices.data.total ?? devices.length;

      const totalFinishers = devices.reduce((sum, device) => {
        const opts = device.device_options || {};
        const mainFinisher =
          opts.finisher === 1 || opts.finisher === true ? 1 : 0;
        const internalFinisher = Number(opts.internal_finisher) || 0;
        return sum + mainFinisher + internalFinisher;
      }, 0);

      const devicesWeight = devices.reduce((sum, device) => {
        return sum + Number(device.model_weight || 0);
      }, 0);
      const extraFromFinishers = totalFinishers * 40;
      const finalWeight = Math.round(devicesWeight + extraFromFinishers);

      const isEx = (abaDevices?.client_market_type ?? devices[0]?.client_market_type) === "EX";

      setAbaForm((prev) => ({
        ...prev,
        used_photocopiers: isEx
          ? "Used and fully functional multifunctional photocopiers incl. options"
          : "",
        photocopiers_sets: String(numberOfDevices),
        finishers_count: String(totalFinishers),
        collis_count: String(numberOfDevices + totalFinishers),
        weight: String(finalWeight),
      }));
    }, [abaDevices]);

    // ── Core: build & send/update AbaNinja invoice ──────────────────────────
    const handleSubmit = async () => {
      setIsSending(true);
      try {
        const devices = abaDevices?.data?.data ?? [];
        if (!devices.length) {
          toast.error("No devices found for this invoice.");
          return;
        }

        const ABA_ACCOUNT_UUID = window.REACT_APP_ABANINJA_ACCOUNT_UUID;
        const ABA_API_KEY = window.REACT_APP_ABANINJA_API_KEY;
        const abaHeaders = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ABA_API_KEY}`,
        };

        // ── 1. Fetch AbaNinja persons & our backend clients in parallel ──
        const [abaPersonsRes] = await Promise.all([
          fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/persons?limit=500`,
            { headers: abaHeaders }
          ),
          axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/clients`),
        ]);

        if (!abaPersonsRes.ok)
          throw new Error("Failed to fetch AbaNinja persons.");
        const abaPersonsData = await abaPersonsRes.json();
        const abaPersons = abaPersonsData?.data ?? [];

        // ── 2. Match receiver — try persons first, then fall back to companies ──
        const invoiceClientUniqueId =
          devices[0]?.client_unique_id ?? devices[0]?.client?.unique_id;

        let matchedAbaAddress = abaPersons.find(
          (p) => String(p.customer_number) === String(invoiceClientUniqueId)
        );
        let isCompanyAddress = false;

        if (!matchedAbaAddress) {
          // Not found among persons — try the companies endpoint
          const abaCompaniesRes = await fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/companies?limit=500`,
            { headers: abaHeaders }
          );
          if (!abaCompaniesRes.ok)
            throw new Error("Failed to fetch AbaNinja companies.");
          const abaCompaniesData = await abaCompaniesRes.json();
          const abaCompanies = abaCompaniesData?.data ?? [];

          matchedAbaAddress = abaCompanies.find(
            (c) => String(c.customer_number) === String(invoiceClientUniqueId)
          );
          if (matchedAbaAddress) isCompanyAddress = true;
        }

        if (!matchedAbaAddress) {
          toast.error(
            `Could not find AbaNinja person or company matching client ID: ${invoiceClientUniqueId}`
          );
          return;
        }

        // ── 3. Fetch bank accounts ──
        const bankRes = await fetch(
          `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/finances/v2/bank-accounts`,
          { headers: abaHeaders }
        );
        if (!bankRes.ok)
          throw new Error("Failed to fetch AbaNinja bank accounts.");
        const bankData = await bankRes.json();
        const bankAccounts = bankData?.data ?? [];

        // ── 4. Determine CH vs EX from client_market_type ──
        const firstDevice = devices[0];
        const marketType =
          abaDevices?.client_market_type ?? firstDevice?.client_market_type;
        const isChCustomer = marketType === "CH";

        const productCode = isChCustomer
          ? firstDevice?.swiss_product_code ?? ""
          : firstDevice?.international_product_code ?? "";

        // ── 5. Find the correct bank ──
        const targetBankName = isChCustomer ? "ZKB CHF" : "ZKB Euro Export";
        const bank = bankAccounts.find((b) =>
          b.name?.toLowerCase().includes(targetBankName.toLowerCase())
        );
        if (!bank?.iban) {
          throw new Error(
            `Bank account "${targetBankName}" with a valid IBAN was not found in AbaNinja. ` +
            `Available banks: ${bankAccounts.map((b) => b.name).join(", ")}`
          );
        }

        // ── 6. Calculate dates ──
        const today = new Date();
        const invoiceDateStr = today.toISOString().split("T")[0];
        let dueDateStr;
        if (isChCustomer) {
          const due = new Date(today);
          due.setDate(due.getDate() + 30);
          dueDateStr = due.toISOString().split("T")[0];
        } else {
          dueDateStr = invoiceDateStr;
        }

        // ── 7. Build invoice positions ──
        const positions = [];
        let posNum = 1;

        // Group devices by model + brand + price (applies to both CH and EX).
        // Devices with identical model, brand and price become one position
        // with quantity = count and positionTotal = singlePrice * count.
        // Description depends on market type:
        //   CH → model + brand + serial_no
        //   EX → model only
        const groupMap = {};
        devices.forEach((device) => {
          const normalizedModel = (device.model ?? "").trim().toLowerCase();
          const normalizedBrand = (device.brand ?? "").trim().toLowerCase();
          const normalizedPrice = parseFloat(device.price) || 0;
          const key = `${normalizedModel}||${normalizedBrand}||${normalizedPrice}`;
          if (!groupMap[key]) {
            groupMap[key] = { device, count: 0 };
          }
          groupMap[key].count += 1;
        });

        Object.values(groupMap).forEach(({ device, count }) => {
          const singlePrice = parseFloat(device.price) || 0;
          const positionTotal = singlePrice * count;
          const description = isChCustomer
            ? `${device.model} ${device.brand} ${device.serial_no}`
            : device.model;

          positions.push({
            kind: "product",
            positionNumber: posNum++,
            productNumber: device.model,
            productDescription: description,
            quantity: count,
            singlePrice,
            positionTotal,
            discount: { amount: 0 },
            vat: { percentage: 0, amount: 0 },
          });
        });

        // ── 8. Calculate document total ──
        const devicePositionsTotal = positions.reduce(
          (sum, p) => sum + (p.positionTotal || 0),
          0
        );

        // ── 8a. Add Fracht Export position for EX clients ──
        if (!isChCustomer && showFrachtExport) {
          const frachtPrice = parseFloat(frachtExportPrice) || 0;
          positions.push({
            kind: "product",
            positionNumber: posNum++,
            productNumber: "Fracht Export",
            productDescription: "Freight",
            quantity: 1,
            singlePrice: frachtPrice,
            positionTotal: frachtPrice,
            discount: { amount: 0 },
            vat: { percentage: 0, amount: 0 },
          });
        }

        const documentTotal = positions.reduce(
          (sum, p) => sum + (p.positionTotal || 0),
          0
        );

        // ── 8b. Footer is now empty — descriptions live in productDescription ──
        const footerText = "";

        // ── 9. Build the AbaNinja invoice payload ──
        const invoicePayload = {
          documents: [
            {
              isTemplate: false,
              receiver: {
                addressUuid:
                  matchedAbaAddress.addresses?.[0]?.uuid ?? undefined,
                ...(isCompanyAddress
                  ? { companyUuid: matchedAbaAddress.uuid }
                  : { personUuid: matchedAbaAddress.uuid }),
              },
              currencyCode: matchedAbaAddress.currency_code || "CHF",
              title: `Invoice - ${invoiceNumber}`,
              reference: "",
             // headerText: abaForm.used_photocopiers,
              invoiceDate: invoiceDateStr,
              dueDate: dueDateStr,
              pricesIncludeVat: false,
              documentTotal,
              paymentInstructions: {
                iban: bank.iban,
              },
              publicNotes: abaForm.used_photocopiers || "",
              terms: [
                `Photocopiers Sets: ${abaForm.photocopiers_sets}`,
                `Finishers/Options: ${abaForm.finishers_count}`,
                `Collis Count: ${abaForm.collis_count}`,
                `Weight: ${abaForm.weight}kg`,
                `Payment Terms: ${abaForm.payment_terms}`,
                `Made In: ${abaForm.made_in}`,
                ...(abaForm.real_price
                  ? [`Real Price: ${abaForm.real_price}`]
                  : []),
              ].join("\n"),
              footerText,
              positions,
            },
          ],
        };

        // ── 10. Check if invoice already exists (abaninja_uuid present = update) ──
        const existingAbaUuid = existingInvoice?.data?.abaninja_uuid;

        if (existingAbaUuid) {
          // ── 10a. PATCH updated invoice to AbaNinja using the UUID ──
          const abaUpdateRes = await fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices/${existingAbaUuid}`,
            {
              method: "PATCH",
              headers: abaHeaders,
              body: JSON.stringify(invoicePayload),
            }
          );
          const abaUpdateResult = await abaUpdateRes.json();
          if (!abaUpdateRes.ok) {
            console.error("AbaNinja update error response:", abaUpdateResult);
            throw new Error(
              abaUpdateResult?.message ||
              abaUpdateResult?.meta?.errors?.[0]?.error ||
              "AbaNinja invoice update failed."
            );
          }
          console.log("AbaNinja invoice updated — uuid:", existingAbaUuid);

          // ── 10b. Sync updated fields to our backend ──
          updateAbaNinjaInvoiceMutation.mutate({
            id: existingInvoice.data.id,
            data: {
              real_price: parseFloat(abaForm.real_price) || null,
              used_photocopiers: abaForm.used_photocopiers,
              photocopiers_sets: abaForm.photocopiers_sets,
              finishers_count: abaForm.finishers_count,
              collis_count: abaForm.collis_count,
              weight: abaForm.weight,
              payment_terms: abaForm.payment_terms,
              made_in: abaForm.made_in,
            },
          });
          closeDialog();
          return;
        }

        // ── 11. POST new invoice to AbaNinja ──
        const abaRes = await fetch(
          `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices`,
          {
            method: "POST",
            headers: abaHeaders,
            body: JSON.stringify(invoicePayload),
          }
        );
        const abaResult = await abaRes.json();
        if (!abaRes.ok) {
          console.error("AbaNinja error response:", abaResult);
          throw new Error(
            abaResult?.message ||
            abaResult?.meta?.errors?.[0]?.error ||
            "AbaNinja invoice creation failed."
          );
        }

        const newAbaDocument = abaResult?.data?.documents?.[0] ?? null;
        const newAbaInvoiceNumber = newAbaDocument?.invoiceNumber ?? null;
        const newAbaUuid = newAbaDocument?.uuid ?? null;
        console.log("AbaNinja invoice created — invoiceNumber:", newAbaInvoiceNumber, "uuid:", newAbaUuid);

        // ── 12. Save new record to our backend ──
        sendToAbaNinjaMutation.mutate({
          invoice_number_id: newAbaInvoiceNumber,
          invoice_number_initial_value: invoiceNumber,
          abaninja_invoice_id: newAbaInvoiceNumber,
          abaninja_uuid: newAbaUuid,
          real_price: parseFloat(abaForm.real_price) || null,
          used_photocopiers: abaForm.used_photocopiers,
          photocopiers_sets: abaForm.photocopiers_sets,
          finishers_count: abaForm.finishers_count,
          collis_count: abaForm.collis_count,
          weight: abaForm.weight,
          payment_terms: abaForm.payment_terms,
          made_in: abaForm.made_in,
        });
      } catch (err) {
        console.error("AbaNinja send error:", err);
        toast.error(err.message || "Failed to send to AbaNinja.");
      } finally {
        setIsSending(false);
      }
    };

    const handlePriceUpdate = async () => {
      const updates = groupedDevices
        .filter((g) => localPrices[g.key] !== g.price)
        .map((g) => ({
          brand: g.brand,
          model: g.model,
          price: localPrices[g.key],
        }));

      if (updates.length === 0 && frachtExportPrice === String(initialFreightPrice)) {
        toast.info("No prices to update.");
        return;
      }

      try {
        if (updates.length > 0) {
          const promises = updates.map((u) =>
            axiosInstance.patch(
              `${window.REACT_APP_API_ENDPOINT}/devices/bulk-price`,
              { invoice_number: invoiceNumber, ...u }
            )
          );
          await Promise.all(promises);
        }

        if (frachtExportPrice !== String(initialFreightPrice)) {
          await axiosInstance.patch(
            `${window.REACT_APP_API_ENDPOINT}/abaninja/update-device-invoice/${invoiceId}`,
            { freight_price: frachtExportPrice }
          );
        }

        queryClient.invalidateQueries(["abaninja-devices"]);
        toast.success("Prices updated successfully!");
      } catch (err) {
        toast.error("Failed to update some prices.");
      }
    };

    const handlePriceKeyDown = (e, index) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const nextInput = document.getElementById(`price-input-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        } else {
          // If it was the last row, maybe blur?
          setEditingKey(null);
        }
      }
    };

    const inputClass =
      "w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";

    const BulkAddDevicesModal = ({ onClose, onSubmit, isLoading }) => {
      const [uniqueIdsText, setUniqueIdsText] = useState("");
      const [pricesText, setPricesText] = useState("");

      const uniqueIds = uniqueIdsText
        .split("\n")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      const prices = pricesText
        .split("\n")
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
        .map((p) => {
          const parsed = parseFloat(p);
          return isNaN(parsed) ? null : parsed;
        });

      const hasMatchingCounts = uniqueIds.length > 0 && uniqueIds.length === prices.length;
      const allPricesValid = prices.every((p) => p !== null && p >= 0);

      const handleSubmit = () => {
        if (uniqueIds.length === 0) {
          toast.error("Please enter at least one unique ID.");
          return;
        }

        if (prices.length !== uniqueIds.length) {
          toast.error(
            `Mismatch: ${uniqueIds.length} unique IDs but only ${prices.length} prices. Please add ${uniqueIds.length - prices.length} more price(s).`
          );
          return;
        }

        if (!allPricesValid) {
          toast.error("All prices must be valid numbers.");
          return;
        }

        onSubmit(uniqueIds, prices);
      };

      return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={onClose}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Add Devices by Unique ID & Price
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unique IDs (1 per line)
                      <span className="text-blue-600 font-semibold ml-2">
                        {uniqueIds.length > 0 ? `${uniqueIds.length}` : ""}
                      </span>
                    </label>
                    <textarea
                      value={uniqueIdsText}
                      onChange={(e) => setUniqueIdsText(e.target.value)}
                      placeholder="Enter unique IDs&#10;one per line"
                      className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prices (1 per line)
                      <span
                        className={`ml-2 font-semibold ${
                          prices.length > 0
                            ? hasMatchingCounts
                              ? "text-green-600"
                              : "text-red-600"
                            : "text-gray-500"
                        }`}
                      >
                        {prices.length > 0 ? `${prices.length}` : ""}
                      </span>
                    </label>
                    <textarea
                      value={pricesText}
                      onChange={(e) => setPricesText(e.target.value)}
                      placeholder="Enter prices&#10;one per line"
                      className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {uniqueIds.length > 0 && (
                  <div className="flex items-center gap-2 mb-4 p-3 rounded-md bg-gray-50 border border-gray-200">
                    {hasMatchingCounts ? (
                      <>
                        <div className="flex-shrink-0">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="text-sm text-green-600 font-medium">
                          Perfect! {uniqueIds.length} unique IDs and {prices.length} prices
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="flex-shrink-0">
                          <XCircleIcon className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="text-sm text-red-600 font-medium">
                          Mismatch: {uniqueIds.length} unique IDs but {prices.length} prices.
                          {prices.length < uniqueIds.length
                            ? ` Add ${uniqueIds.length - prices.length} more price(s).`
                            : ` Remove ${prices.length - uniqueIds.length} price(s).`}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isLoading || !hasMatchingCounts || allPricesValid === false}
                >
                  {isLoading ? "Processing..." : "Add Devices"}
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    if (checkingExisting) {
      return (
        <div className="flex items-center justify-center py-8">
          <LoadingComponent />
          <span className="ml-2 text-gray-600">
            Checking existing invoice...
          </span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {existingInvoice?.data && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> This invoice already exists in AbaNinja
                  (ID: {existingInvoice.data.abaninja_invoice_id}). Clicking
                  "Update in AbaNinja" will update the invoice both in AbaNinja
                  and in the local record.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end mb-4">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => setShowBulkAddDialog(true)}
          >
            Update Prices by Device ID
          </Button>
        </div>

        {showBulkAddDialog && (
          <BulkAddDevicesModal
            onClose={() => setShowBulkAddDialog(false)}
            onSubmit={(uniqueIds, prices) =>
              bulkUpdateByUniqueIdMutation.mutate({ uniqueIds, prices })
            }
            isLoading={bulkUpdateByUniqueIdMutation.isPending}
          />
        )}

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          {abaLoading ? (
            <LoadingComponent />
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Brand
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Model
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Price per Unit
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {groupedDevices.length > 0 ? (
                  <>
                    {groupedDevices.map((group, i) => (
                      <tr
                        key={group.key}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {group.brand || "—"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {group.model || "—"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                          {group.count}
                        </td>
                        <td
                          className="px-4 py-2 text-sm text-gray-900"
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById(`price-input-${i}`)?.focus();
                          }}
                        >
                          <input
                            id={`price-input-${i}`}
                            type="number"
                            step="0.01"
                            value={localPrices[group.key] ?? ""}
                            onChange={(e) =>
                              setLocalPrices((p) => ({
                                ...p,
                                [group.key]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => handlePriceKeyDown(e, i)}
                            className="w-full border-0 p-0 focus:ring-0 text-sm bg-transparent"
                            placeholder="Set Price"
                          />
                        </td>
                      </tr>
                    ))}
                    {isExCustomer && showFrachtExport && (
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-4 py-2 text-sm font-medium text-blue-800 flex items-center gap-2">
                          Fracht Export
                          <button
                            onClick={() => setShowFrachtExport(false)}
                            className="text-red-500 hover:text-red-700"
                            title="Remove Freight Export"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-blue-700">
                          Freight
                        </td>
                        <td className="px-4 py-2 text-sm text-blue-700 font-medium">
                          1
                        </td>
                        <td
                          className="px-4 py-2 text-sm text-blue-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="number"
                            step="0.01"
                            value={frachtExportPrice}
                            onChange={(e) => setFrachtExportPrice(e.target.value)}
                            className="w-full border-0 p-0 focus:ring-0 text-sm bg-transparent"
                            placeholder="Set Price"
                          />
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-100 font-semibold">
                      <td
                        colSpan={2}
                        className="px-4 py-2 text-sm text-gray-700 text-right"
                      >
                        Total devices:
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {abaDevices?.data?.total ?? abaDevices?.data?.data?.length ?? 0}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={handlePriceUpdate}
                        >
                          Update Prices
                        </Button>
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      <NoData message="No devices found" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3">
          {[
            {
              key: "used_photocopiers",
              label:
                "Public Notes",
            },
            {
              key: "photocopiers_sets",
              label: "Photocopiers sets (number of devices)",
            },
            {
              key: "finishers_count",
              label: "Finishers/Options (number of Finishers)",
            },
            {
              key: "collis_count",
              label: "Collis (number of Devices + number of Finishers)",
            },
            {
              key: "weight",
              label: "Weight (total weight, + finisher 1 ≈ 40kg)",
            },
            { key: "payment_terms", label: "Payment terms" },
            { key: "made_in", label: "Made in" },
          ].map(({ key, label }, index, array) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {label}
              </label>
              <input
                id={`abaninja-input-${index}`}
                type="text"
                value={abaForm[key]}
                onChange={(e) =>
                  setAbaForm((p) => ({ ...p, [key]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (index < array.length - 1) {
                      document
                        .getElementById(`abaninja-input-${index + 1}`)
                        ?.focus();
                    } else {
                      document
                        .getElementById("abaninja-submit-btn")
                        ?.focus();
                    }
                  }
                }}
                className={inputClass}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            id="abaninja-submit-btn"
            variant={existingInvoice?.data ? "warning" : "primary"}
            size="sm"
            onClick={handleSubmit}
            disabled={
              isSending ||
              sendToAbaNinjaMutation.isPending ||
              updateAbaNinjaInvoiceMutation.isPending
            }
          >
            {isSending ||
              sendToAbaNinjaMutation.isPending ||
              updateAbaNinjaInvoiceMutation.isPending
              ? existingInvoice?.data
                ? "Updating..."
                : "Sending..."
              : existingInvoice?.data
                ? "Update in AbaNinja"
                : "Send to AbaNinja"}
          </Button>
        </div>
      </div>
    );
  };

  // ── Rest of component ──────────────────────────────────────────────────────

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) =>
      data.data.data.map(({ id, name }) => ({ id: id.toString(), name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id: id.toString(),
        name,
        device_brand_id,
      })),
  }).data;

  const paginationData = {
    current_page: devicesData?.current_page || 1,
    last_page: devicesData?.last_page || 1,
    next_page_url: devicesData?.next_page_url,
    prev_page_url: devicesData?.prev_page_url,
  };

  const resetConfirmationMutation = useMutation({
    mutationFn: (invoiceNumber) => {
      if (!invoiceNumber) {
        toast.error("Invoice number is required");
        return Promise.reject(new Error("Invoice number is required"));
      }
      return axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/reset-confirmation/${invoiceNumber}`
      );
    },
    onSuccess: (response) => {
      const { message, updated_count } = response.data;
      toast.success(message);
      if (updated_count > 0) {
        queryClient.invalidateQueries(["reserved-devices"]);
        queryClient.invalidateQueries(["invoiceDetails"]);
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
        "An error occurred while resetting confirmation"
      );
      console.error("Reset confirmation error:", error);
    },
  });

  function handleOCR(unique_id, confirmed, client_name, id) {
    return openDialog({
      title: "OCR",
      content: (
        <QReader
          unique_id={unique_id}
          confirmed={confirmed}
          id={id}
          client_name={client_name}
          onClose={() => {
            queryClient.invalidateQueries(["reserved-devices"]);
            closeDialog();
          }}
        />
      ),
    });
  }

  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["recycledDevices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete device error:", error);
    },
  });

  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  // ── Mark as Sold flow ──────────────────────────────────────────────────────
  // Step 1: ask the user if they want to create/update a Delivery Note first.
  // Step 2a (Yes): open DeliveryNoteModal; on successful submit → mark as sold.
  // Step 2b (No):  show the normal confirmation dialog → mark as sold.
  const handleMarkAsSold = (invoiceNumber, e) => {
    e.stopPropagation();

    // Step 1 — delivery note prompt
    openDialog({
      title: "Mark as Sold",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-base text-gray-700">
            Do you want to create or modify a{" "}
            <span className="font-semibold">Delivery Note</span> before marking
            this stock as sold?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="light"
              size="sm"
              onClick={() => closeDialog()}
            >
              Cancel
            </Button>
            {/* No → skip delivery note, go straight to confirm */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                openDialog({
                  title: "Mark as Sold",
                  content: (
                    <div className="flex flex-col gap-4">
                      <p className="text-lg">
                        Are you sure you want to mark this stock as sold?
                      </p>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => closeDialog()}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => {
                            markAsSoldMutation.mutate(invoiceNumber);
                            closeDialog();
                          }}
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  ),
                });
              }}
            >
              No, just mark as sold
            </Button>
            {/* Yes → open delivery note modal; after submit, mark as sold */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                openDialog({
                  title: `Delivery Notes — ${invoiceNumber}`,
                  content: (
                    <DeliveryNoteModal
                      invoiceNumber={invoiceNumber}
                      onClose={closeDialog}
                      onSubmitSuccess={() => {
                        // Delivery note saved — now mark as sold
                        markAsSoldMutation.mutate(invoiceNumber);
                        closeDialog();
                      }}
                    />
                  ),
                });
              }}
            >
              Yes, open Delivery Note
            </Button>
          </div>
        </div>
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>

        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Reserved Stocks
                </h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    icon={excelIcon}
                    onClick={() => exportCSVData()}
                    className="mb-2 lg:mb-0"
                  >
                    Export Reserved Stock Data as CSV
                  </Button>
                  <Button
                    href="/admin/stock/reserve"
                    variant="outline"
                    size="sm"
                    icon={<PlusIcon className="h-4 w-4" />}
                  >
                    Reserve a Stock
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Client ID"
                            columnKey="client_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Client ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Client"
                            columnKey="client"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Client..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Invoice Number"
                            columnKey="invoice_number"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Invoice..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Reserved Date"
                            columnKey="reserved_date"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            isDate={true}
                            placeholder="Search by Reserved Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          Total Items Reserved
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm"></th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={14} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : devicesData?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center">
                          <NoData message="No reserved devices found" />
                        </td>
                      </tr>
                    ) : (
                      devicesData?.data?.map((deviceStatus, index) => (
                        <React.Fragment key={deviceStatus.invoice_number}>
                          <tr
                            className={`cursor-pointer hover:bg-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }`}
                            onClick={() =>
                              handleDisclosureToggle(
                                deviceStatus.invoice_number,
                                deviceStatus.invoice_id
                              )
                            }
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {deviceStatus?.client.unique_id || "---"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 h-full whitespace-nowrap items-center">
                              {deviceStatus?.client.name || "---"}
                            </td>
                            <td
                              onClick={(e) => e.stopPropagation()}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              <EditableCell
                                value={deviceStatus.invoice_number || ""}
                                displayValue={deviceStatus.invoice_number || ""}
                                id={deviceStatus.id}
                                field="invoice_number"
                                onSave={(data) =>
                                  editStatusCellMutation.mutate({
                                    invoiceNumber: deviceStatus.invoice_number,
                                    field: "invoice_number",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td
                              onClick={(e) => e.stopPropagation()}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              <EditableDateCell
                                value={deviceStatus.reserved_date}
                                displayValue={
                                  deviceStatus.reserved_date
                                    ? dayjs
                                      .utc(deviceStatus.reserved_date)
                                      .format("DD/MM/YYYY")
                                    : "---"
                                }
                                id={deviceStatus.id}
                                field="reserved_date"
                                onSave={(data) =>
                                  editStatusCellMutation.mutate({
                                    invoiceNumber: deviceStatus.invoice_number,
                                    field: "reserved_date",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {deviceStatus.total_devices || "---"}
                            </td>
                            <td className="px-4 py-3 text-gray-900">
                              <ChevronUpIcon
                                className={`h-5 w-5 transform transition-transform duration-200 ${openDisclosure?.invoiceId ===
                                  deviceStatus.invoice_id
                                  ? "rotate-180"
                                  : ""
                                  }`}
                              />
                            </td>
                            <td className="px-4 py-3 text-gray-900">
                              {openDisclosure?.invoiceId ===
                                deviceStatus.invoice_id &&
                                invoiceDetails?.data?.data?.length > 0 && (
                                  <div className="flex gap-2 flex-wrap">
                                    <Button
                                      variant="light-success"
                                      size="sm"
                                      icon={
                                        <CheckCircleIcon className="h-4 w-4" />
                                      }
                                      disabled={invoiceDetails?.data?.data.some(
                                        (device) => device.confirmed !== 1
                                      )}
                                      onClick={(e) => {
                                        handleMarkAsSold(
                                          deviceStatus.invoice_number,
                                          e
                                        );
                                      }}
                                    >
                                      Mark as Sold
                                    </Button>
                                    <Button
                                      variant="light-danger"
                                      size="sm"
                                      icon={<XMarkIcon className="h-4 w-4" />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: "Delete Reserved Stock",
                                          content: (
                                            <div className="flex flex-col gap-4">
                                              <p className="text-lg">
                                                Are you sure you want to delete
                                                this reserved stock?
                                              </p>
                                              <div className="flex items-center justify-end gap-2">
                                                <Button
                                                  variant="light"
                                                  size="sm"
                                                  onClick={() => closeDialog()}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  variant="danger"
                                                  size="sm"
                                                  onClick={() => {
                                                    removeReservationMutation.mutate(
                                                      deviceStatus.invoice_number
                                                    );
                                                    closeDialog();
                                                  }}
                                                >
                                                  Confirm
                                                </Button>
                                              </div>
                                            </div>
                                          ),
                                        });
                                      }}
                                    >
                                      Remove Reservation
                                    </Button>
                                    <Button
                                      variant="light-info"
                                      size="sm"
                                      icon={<QrCodeIcon className="h-4 w-4" />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: "Scan QR Code",
                                          content: (
                                            <InvoiceQRScanner
                                              invoiceNumber={
                                                deviceStatus.invoice_number
                                              }
                                              shouldCloseButton={
                                                Array.isArray(
                                                  invoiceDetails.data.data
                                                ) &&
                                                invoiceDetails.data.data.every(
                                                  (device) =>
                                                    device.confirmed === 1
                                                )
                                              }
                                            />
                                          ),
                                        });
                                      }}
                                    >
                                      Verify
                                    </Button>
                                    <Button
                                      variant="light-orange"
                                      size="sm"
                                      icon={
                                        <ArrowPathIcon className="h-4 w-4" />
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        resetConfirmationMutation.mutate(
                                          deviceStatus.invoice_number
                                        );
                                      }}
                                    >
                                      Reset Verify
                                    </Button>
                                    <Button
                                      variant="light-success"
                                      size="sm"
                                      imgIcon={excelIcon}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        exportCSVData(
                                          deviceStatus.invoice_number
                                        );
                                      }}
                                      className="mb-2 lg:mb-0"
                                    >
                                      Export By Invoice Number
                                    </Button>
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: `Send To AbaNinja — ${deviceStatus.invoice_number}`,
                                          content: (
                                            <AbaNinjaModal
                                              invoiceNumber={
                                                deviceStatus.invoice_number
                                              }
                                              invoiceId={
                                                deviceStatus.invoice_id
                                              }
                                              initialFreightPrice={
                                                deviceStatus.freight_price ?? ""
                                              }
                                            />
                                          ),
                                        });
                                      }}
                                    >
                                      Send To AbaNinja
                                    </Button>
                                    {/* ── Delivery Notes Button ── */}
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: `Delivery Notes — ${deviceStatus.invoice_number}`,
                                          content: (
                                            <DeliveryNoteModal
                                              invoiceNumber={
                                                deviceStatus.invoice_number
                                              }
                                              onClose={closeDialog}
                                            />
                                          ),
                                        });
                                      }}
                                    >
                                      Delivery Notes
                                    </Button>

                                    {/* ── Add to Reservation Button ── */}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: `Add to this reservation`,
                                          content: (
                                            <AddToReservationModal
                                              invoiceNumber={
                                                deviceStatus.invoice_number
                                              }
                                              onClose={closeDialog}
                                            />
                                          ),
                                        });
                                      }}
                                    >
                                      Add to this reservation
                                    </Button>
                                  </div>
                                )}
                            </td>
                          </tr>

                          {openDisclosure?.invoiceId ===
                            deviceStatus.invoice_id && (
                              <tr>
                                <td
                                  colSpan={13}
                                  className="bg-gray-300 px-6 py-4"
                                >
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-bluePrimary text-white">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Unique ID
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Brand
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Model
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Serial No
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Counter Black
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Counter Color
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Total Counter
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Palette
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Registered Date
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Date Stayed
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Options
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Comment
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Reserved Date
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Price
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                          Verified
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {invoiceDetailsLoading ? (
                                        <tr>
                                          <td
                                            colSpan={15}
                                            className="text-center"
                                          >
                                            <LoadingComponent />
                                          </td>
                                        </tr>
                                      ) : Array.isArray(
                                        invoiceDetails?.data?.data
                                      ) ? (
                                        invoiceDetails?.data?.data?.map(
                                          (device, i) => (
                                            <tr
                                              key={i}
                                              className="hover:bg-gray-50"
                                            >
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {device.unique_id}
                                              </td>
                                              <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                                                <div className="flex items-center pl-4">
                                                  {brands && device.brand && (
                                                    <EditableSelectCell
                                                      value={
                                                        device.device_brand_id
                                                      }
                                                      displayValue={device.brand}
                                                      id={device.id}
                                                      options={brands}
                                                      field="brand"
                                                      onSave={(data) =>
                                                        editCellMutation.mutate({
                                                          deviceId: data.id,
                                                          field: "device_brand_id",
                                                          value: data.value,
                                                        })
                                                      }
                                                    />
                                                  )}
                                                </div>
                                              </td>
                                              <td className="whitespace-nowrap p-3 text-sm text-black">
                                                {models && device.model && (
                                                  <EditableSelectCell
                                                    value={device.device_model_id}
                                                    displayValue={device.model}
                                                    id={device.id}
                                                    options={models.filter(
                                                      (model) =>
                                                        model.device_brand_id ===
                                                        device.device_brand_id
                                                    )}
                                                    field="device_model_id"
                                                    onSave={(data) =>
                                                      editCellMutation.mutate({
                                                        deviceId: data.id,
                                                        field: "device_model_id",
                                                        value: data.value,
                                                      })
                                                    }
                                                  />
                                                )}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditSerialNumberCell
                                                  value={device.serial_no}
                                                  displayValue={device.serial_no}
                                                  id={device.id}
                                                  field="serial_no"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "serial_no",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.counter_black}
                                                  displayValue={
                                                    device.counter_black || "---"
                                                  }
                                                  id={device.id}
                                                  field="counter_black"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "counter_black",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.counter_color}
                                                  displayValue={
                                                    device.counter_color || "---"
                                                  }
                                                  id={device.id}
                                                  field="counter_color"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "counter_color",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.total_counter}
                                                  displayValue={
                                                    device.total_counter || "---"
                                                  }
                                                  id={device.id}
                                                  field="total_counter"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "total_counter",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.palette ?? null}
                                                  displayValue={
                                                    device.palette || "---"
                                                  }
                                                  id={device.id}
                                                  field="palette"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "palette",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {dayjs
                                                  .utc(device.created_at)
                                                  .format("DD/MM/YYYY")}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {device.reserved_date &&
                                                  device.created_at
                                                  ? `${dayjs
                                                    .utc(device.reserved_date)
                                                    .diff(
                                                      dayjs.utc(
                                                        device.created_at
                                                      ),
                                                      "days"
                                                    )} days`
                                                  : "---"}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableOptionsCell
                                                  deviceId={device.id}
                                                  deviceOptions={
                                                    device.device_options || {}
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.comment || ""}
                                                  displayValue={
                                                    device.comment || ""
                                                  }
                                                  id={device.id}
                                                  field="comment"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "comment",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {device.reserved_date
                                                  ? `${dayjs
                                                    .utc(device.reserved_date)
                                                    .format("DD/MM/YYYY")}`
                                                  : "---"}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                <EditableCell
                                                  value={device.price || ""}
                                                  displayValue={
                                                    device.price || "---"
                                                  }
                                                  id={device.id}
                                                  field="price"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "price",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              </td>
                                              <td className="px-4 py-2 text-sm flex items-center gap-2 text-gray-900">
                                                {device.confirmed === 1 ? (
                                                  <>
                                                    <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                                                      Verified
                                                    </span>
                                                    <button
                                                      onClick={() => {
                                                        handleOCR(
                                                          device.unique_id,
                                                          true,
                                                          deviceStatus?.client.id
                                                            ?.name || "---",
                                                          device.device_status_id
                                                        );
                                                      }}
                                                      className="bg-gray-200 text-gray-800 rounded px-2 py-1 text-xs hover:bg-gray-300"
                                                    >
                                                      Recheck
                                                    </button>
                                                  </>
                                                ) : (
                                                  <span className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                                                    Not Verified
                                                  </span>
                                                )}
                                                <button
                                                  onClick={() => {
                                                    openDialog({
                                                      title:
                                                        "Cancel Device Reservation",
                                                      content: (
                                                        <div>
                                                          <p>
                                                            Are you sure you want
                                                            to cancel the
                                                            reservation for this
                                                            device?
                                                          </p>
                                                          <div className="flex justify-end gap-2 mt-4">
                                                            <button
                                                              className="bg-gray-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-300 text-gray-600"
                                                              onClick={() =>
                                                                closeDialog()
                                                              }
                                                            >
                                                              Cancel
                                                            </button>
                                                            <button
                                                              className="bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-red-300 text-red-600"
                                                              onClick={() => {
                                                                deleteSingleDeviceMutation.mutate(
                                                                  device.device_status_id
                                                                );
                                                                closeDialog();
                                                              }}
                                                            >
                                                              Confirm
                                                            </button>
                                                          </div>
                                                        </div>
                                                      ),
                                                    });
                                                  }}
                                                  className="bg-orange-100 flex gap-2 items-center text-orange-800 rounded px-2 py-1 text-xs hover:bg-orange-200"
                                                >
                                                  Cancel Reservation
                                                </button>
                                                <ActionButtons
                                                  id={device.id}
                                                  onDelete={() =>
                                                    handleDelete(device.id)
                                                  }
                                                />
                                              </td>
                                            </tr>
                                          )
                                        )
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={15}
                                            className="text-center"
                                          >
                                            <NoData message="No devices found for this invoice" />
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={paginationData.current_page}
                last_page={paginationData.last_page}
                next_page_url={paginationData.next_page_url}
                prev_page_url={paginationData.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}